// src/components/wizard/StepMapping/RuleBuilderModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Settings2, X, Layers, Plus, ArrowDown, Scissors,
  Calendar, Hash, ChevronUp, ChevronDown, Brackets, Pin, Shield,
  Trash2, Database, Lightbulb
} from 'lucide-react';
import { ruleCatalog, isMissing } from '../../../utils/etlMappingUtils';
import { useWizardStore } from '../../../store/useWizardStore';
import { RULE_EXAMPLES } from '../../../utils/transformRules';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { PipelinePreview } from './PipelinePreview';


// export function RuleBuilderModal({ activeTransform, localMap, setLocalMap, onClose, onClearPipeline, onForceModalRequest }) {
export function RuleBuilderModal({ activeTransform, localMap, setLocalMap, onClose, onClearPipeline, onForceModalRequest, targetAvailableTables, onFetchLookupColumns, sourceColumns, source, target }) {
  const [openCategories, setOpenCategories] = useState({ text: false, number: false, date: false, cond: false, fixed: false, security: false, lookup: false });
  const [lookupColumnsCache, setLookupColumnsCache] = useState({});
  const [loadingLookupCols, setLoadingLookupCols] = useState(false);
  const [openExampleIdx, setOpenExampleIdx] = useState(null);

  const { t } = useLanguageStore();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // ── Rehidratar cache de columnas para LOOKUPs existentes al reabrir ──
  useEffect(() => {
    if (!activeTransform || !onFetchLookupColumns) return;
    const pipeline = localMap[activeTransform]?.pipeline || [];
    const lookupTables = pipeline
      .filter(r => r.id === 'LOOKUP' && r.params?.lookupTable)
      .map(r => r.params.lookupTable)
      .filter(tbl => !lookupColumnsCache[tbl]);

    if (lookupTables.length === 0) return;

    let cancelled = false;
    const hydrate = async () => {
      setLoadingLookupCols(true);
      try {
        for (const tbl of lookupTables) {
          if (cancelled) break;
          const cols = await onFetchLookupColumns(tbl);
          if (!cancelled) {
            setLookupColumnsCache(prev => ({ ...prev, [tbl]: cols || [] }));
          }
        }
      } catch (err) {
        console.error('Error hydrating lookup columns:', err);
      } finally {
        if (!cancelled) setLoadingLookupCols(false);
      }
    };
    hydrate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTransform]);

  const toggleCategory = (cat) => setOpenCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  const executeAddRule = (ruleDef, forceClear = false) => {
    setLocalMap(prev => {
      const colData = prev[activeTransform];
      let currentPipeline = forceClear ? [] : [...(colData.pipeline || [])];
      const casingRules = ['UPPERCASE', 'LOWERCASE', 'PROPER', 'CAPITALIZE_FIRST'];
      if (casingRules.includes(ruleDef.id)) {
        currentPipeline = currentPipeline.filter(r => !casingRules.includes(r.id));
      }
      const newPipeline = [...currentPipeline, { id: ruleDef.id, label: ruleDef.label, params: {} }];
      return { ...prev, [activeTransform]: { ...colData, type: 'transform', pipeline: newPipeline } };
    });
  };

  const requestAddRule = (ruleDef) => {
    const currentPipeline = localMap[activeTransform]?.pipeline || [];
    if (ruleDef.id === 'SET_FIXED' && currentPipeline.length > 0) {
      onForceModalRequest(() => executeAddRule(ruleDef, true));
    } else {
      executeAddRule(ruleDef, false);
    }
  };

  const removeRuleFromPipeline = (index) => {
    setLocalMap(prev => {
      const colData = prev[activeTransform];
      const newPipeline = [...colData.pipeline];
      newPipeline.splice(index, 1);
      const newType = (newPipeline.length === 0 && colData.source && colData.source !== 'IGNORE') ? 'direct' : 'transform';
      return { ...prev, [activeTransform]: { ...colData, type: newType, pipeline: newPipeline } };
    });
  };

  const moveRule = (index, direction) => {
    setLocalMap(prev => {
      const colData = prev[activeTransform];
      const newPipeline = [...colData.pipeline];
      if (index + direction < 0 || index + direction >= newPipeline.length) return prev;
      const temp = newPipeline[index];
      newPipeline[index] = newPipeline[index + direction];
      newPipeline[index + direction] = temp;
      return { ...prev, [activeTransform]: { ...colData, pipeline: newPipeline } };
    });
  };

  const updateRuleParam = (index, key, value) => {
    setLocalMap(prev => {
      const colData = prev[activeTransform];
      const newPipeline = [...colData.pipeline];
      newPipeline[index].params = { ...newPipeline[index].params, [key]: value };
      return { ...prev, [activeTransform]: { ...colData, pipeline: newPipeline } };
    });
  };

  const handleStrictInt = (index, key, rawValue, maxVal) => {
    let v = rawValue.replace(/[^0-9]/g, '');
    if (v === '') {
      updateRuleParam(index, key, '');
      return;
    }
    let num = parseInt(v, 10);
    if (num === 0) return;
    if (num > maxVal) num = maxVal;
    updateRuleParam(index, key, num.toString());
  };

  const handleLookupTableChange = async (index, tableName) => {
    updateRuleParam(index, 'lookupTable', tableName);
    updateRuleParam(index, 'lookupField', '');
    updateRuleParam(index, 'returnField', '');
    updateRuleParam(index, 'filterField', '');
    updateRuleParam(index, 'filterSource', '');
    updateRuleParam(index, 'filterValue', '');
    updateRuleParam(index, 'defaultValue', '');

    if (!tableName || lookupColumnsCache[tableName]) return;

    if (onFetchLookupColumns) {
      setLoadingLookupCols(true);
      try {
        const cols = await onFetchLookupColumns(tableName);
        setLookupColumnsCache(prev => ({ ...prev, [tableName]: cols || [] }));
      } catch (err) {
        console.error('Error fetching lookup columns:', err);
        setLookupColumnsCache(prev => ({ ...prev, [tableName]: [] }));
      } finally {
        setLoadingLookupCols(false);
      }
    }
  };

  const renderRuleInputs = (rule, index) => {
    if (rule.id === 'REPLACE') return (
      <div className="grid grid-cols-1 gap-2 mt-1 bg-slate-50 p-2 rounded-lg border border-slate-100">
        <div>
          <label className="text-[10px] font-bold text-slate-500 uppercase ml-1">{t('wizard.mapping.rules.search')} {isMissing(rule.params.search) && <span className="text-red-500">*</span>}</label>
          <input type="text" placeholder={t('wizard.mapping.rules.original_text')} value={rule.params.search || ''} onChange={e => updateRuleParam(index, 'search', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.search) ? 'border-red-300 focus:border-red-500' : 'focus:border-indigo-500'}`} />
        </div>
        <div>
          <label className="text-[10px] font-bold text-slate-500 uppercase ml-1">{t('wizard.mapping.rules.replace')}</label>
          <input type="text" placeholder={t('wizard.mapping.rules.new_val')} value={rule.params.replace || ''} onChange={e => updateRuleParam(index, 'replace', e.target.value)} className="w-full text-xs border rounded p-2 outline-none focus:border-indigo-500 mt-1" />
        </div>
      </div>
    );
    if (['ADD_PREFIX', 'ADD_SUFFIX'].includes(rule.id)) return (
      <div className="mt-1 bg-slate-50 p-2 rounded-lg border border-slate-200">
        <label className="text-[10px] font-bold text-slate-700 uppercase ml-1">{t('wizard.mapping.rules.text_to_add')} {isMissing(rule.params.text) && <span className="text-red-500">*</span>}</label>
        <input type="text" placeholder={rule.id === 'ADD_PREFIX' ? "Ej: DNI " : "Ej:  USD"} value={rule.params.text || ''} onChange={e => updateRuleParam(index, 'text', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.text) ? 'border-red-300 focus:border-red-500' : 'focus:border-slate-500'}`} />
      </div>
    );
    if (rule.id === 'MATH_MULTIPLY') return (
      <div className="mt-1 bg-blue-50/50 p-2 rounded-lg border border-blue-100">
        <label className="text-[10px] font-bold text-blue-700 uppercase ml-1">{t('wizard.mapping.rules.num_val')} {isMissing(rule.params.value) && <span className="text-red-500">*</span>}</label>
        <input type="number" placeholder="Ej: 100" value={rule.params.value || ''} onChange={e => updateRuleParam(index, 'value', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.value) ? 'border-red-300 focus:border-red-500' : 'focus:border-blue-500'}`} />
      </div>
    );
    if (rule.id === 'MATH_DIVIDE') return (
      <div className="mt-1 bg-blue-50/50 p-2 rounded-lg border border-blue-100">
        <label className="text-[10px] font-bold text-blue-700 uppercase ml-1">{t('wizard.mapping.rules.num_val')} {(isMissing(rule.params.value) || parseFloat(rule.params.value) === 0) && <span className="text-red-500">* (Requerido y distinto a 0)</span>}</label>
        <input type="number" placeholder="Ej: 100" value={rule.params.value || ''} onChange={e => updateRuleParam(index, 'value', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${(isMissing(rule.params.value) || parseFloat(rule.params.value) === 0) ? 'border-red-300 focus:border-red-500' : 'focus:border-blue-500'}`} />
      </div>
    );
    if (rule.id === 'DEFAULT_NULL') return (
      <div className="mt-1 bg-slate-50 p-3 rounded-lg border border-slate-200 flex flex-col gap-3">
        <div>
          <label className="text-[10px] font-bold text-slate-700 uppercase ml-1">{t('wizard.mapping.rules.detect')}</label>
          <select
            value={rule.params.detectType || 'both'}
            onChange={e => updateRuleParam(index, 'detectType', e.target.value)}
            className="w-full text-xs border rounded p-2 outline-none mt-1 bg-white focus:border-slate-500"
          >
            <option value="both">{t('wizard.mapping.rules.both')}</option>
            <option value="null">{t('wizard.mapping.rules.only_null')}</option>
            <option value="empty">{t('wizard.mapping.rules.only_empty')}</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold text-slate-700 uppercase ml-1">{t('wizard.mapping.rules.default_val')} {isMissing(rule.params.value) && <span className="text-red-500">*</span>}</label>
          <input type="text" placeholder="Ej: N/A" value={rule.params.value || ''} onChange={e => updateRuleParam(index, 'value', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.value) ? 'border-red-300 focus:border-red-500' : 'focus:border-slate-500'}`} />
        </div>
      </div>
    );
    if (rule.id === 'MATH_ROUND') return (
      <div className="mt-1 bg-blue-50/50 p-2 rounded-lg border border-blue-100">
        <label className="text-[10px] font-bold text-blue-700 uppercase ml-1">{t('wizard.mapping.rules.decimals')} {isMissing(rule.params.decimals) && <span className="text-red-500">*</span>}</label>
        <input type="number" placeholder="Ej: 2" value={rule.params.decimals || ''} onChange={e => handleStrictInt(index, 'decimals', e.target.value, 10)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.decimals) ? 'border-red-300 focus:border-red-500' : 'focus:border-blue-500'}`} />
      </div>
    );
    if (rule.id === 'MATH_ADD_SUB') return (
      <div className="mt-1 bg-blue-50/50 p-2 rounded-lg border border-blue-100">
        <label className="text-[10px] font-bold text-blue-700 uppercase ml-1">{t('wizard.mapping.rules.add_sub')} {isMissing(rule.params.value) && <span className="text-red-500">*</span>}</label>
        <input type="number" placeholder="Ej: -10" value={rule.params.value || ''} onChange={e => updateRuleParam(index, 'value', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.value) ? 'border-red-300 focus:border-red-500' : 'focus:border-blue-500'}`} />
      </div>
    );
    if (rule.id === 'DATE_EXTRACT') return (
      <div className="mt-1 bg-amber-50/30 p-2 rounded-lg border border-amber-100">
        <label className="text-[10px] font-bold text-amber-700 uppercase ml-1">{t('wizard.mapping.rules.extract_part')} {isMissing(rule.params.part) && <span className="text-red-500">*</span>}</label>
        <select value={rule.params.part || ''} onChange={e => updateRuleParam(index, 'part', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none mt-1 bg-white ${isMissing(rule.params.part) ? 'border-red-300 focus:border-red-500' : 'focus:border-amber-500'}`}>
          <option value="">-- Seleccionar --</option>
          <option value="YEAR">{t('wizard.mapping.options.year') || 'Solo el Año'}</option>
          <option value="MONTH">{t('wizard.mapping.options.month') || 'Solo el Mes'}</option>
          <option value="DAY">{t('wizard.mapping.options.day') || 'Solo el Día'}</option>
        </select>
      </div>
    );
    if (rule.id === 'TRUNCATE') return (
      <div className="mt-1 bg-emerald-50 p-2 rounded-lg border border-emerald-100">
        <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1">{t('wizard.mapping.rules.max_length')} {isMissing(rule.params.max_length) && <span className="text-red-500">*</span>}</label>
        <input type="text" placeholder="Ej: 50" value={rule.params.max_length || ''} onChange={e => handleStrictInt(index, 'max_length', e.target.value, 9999)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.max_length) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`} />
      </div>
    );
    if (rule.id === 'SUBSTRING') return (
      <div className="grid grid-cols-2 gap-2 mt-1 bg-emerald-50 p-2 rounded-lg border border-emerald-100">
        <div className="min-w-0">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1">{t('wizard.mapping.rules.start_pos')} {isMissing(rule.params.start) && <span className="text-red-500">*</span>}</label>
          <input type="text" placeholder="Ej: 1" value={rule.params.start || ''} onChange={e => handleStrictInt(index, 'start', e.target.value, 9999)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.start) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`} />
        </div>
        <div className="min-w-0">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1">{t('wizard.mapping.rules.end_pos')} {isMissing(rule.params.end) && <span className="text-red-500">*</span>}</label>
          <input type="text" placeholder="Ej: 5" value={rule.params.end || ''} onChange={e => handleStrictInt(index, 'end', e.target.value, 9999)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.end) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`} />
        </div>
      </div>
    );
    if (rule.id === 'EXTRACT_END') return (
      <div className="mt-1 bg-emerald-50 p-2 rounded-lg border border-emerald-100">
        <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1">{t('wizard.mapping.rules.char_count')} {isMissing(rule.params.length) && <span className="text-red-500">*</span>}</label>
        <input type="text" placeholder="Ej: 4" value={rule.params.length || ''} onChange={e => handleStrictInt(index, 'length', e.target.value, 9999)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.length) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`} />
      </div>
    );
    if (rule.id === 'SPLIT_GET') return (
      <div className="grid grid-cols-2 gap-2 mt-1 bg-emerald-50 p-2 rounded-lg border border-emerald-100">
        <div className="min-w-0">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1">{t('wizard.mapping.rules.extract_part')} {isMissing(rule.params.part) && <span className="text-red-500">*</span>}</label>
          <select value={rule.params.part || ''} onChange={e => updateRuleParam(index, 'part', e.target.value)} className={`w-full truncate text-xs border rounded p-2 outline-none mt-1 bg-white ${isMissing(rule.params.part) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`}>
            <option value="">-- Seleccionar --</option>
            <option value="BEFORE">{t('wizard.mapping.rules.before')}</option>
            <option value="AFTER">{t('wizard.mapping.rules.after')}</option>
          </select>
        </div>
        <div className="min-w-0">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1">{t('wizard.mapping.rules.delimiter')} {isMissing(rule.params.delimiter) && <span className="text-red-500">*</span>}</label>
          <input type="text" placeholder="Ej: @" value={rule.params.delimiter || ''} onChange={e => updateRuleParam(index, 'delimiter', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.delimiter) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`} />
        </div>
      </div>
    );
    if (rule.id === 'FORMAT_DATE') return (
      <div className="grid grid-cols-2 gap-2 mt-1 bg-amber-50/30 p-2 rounded-lg border border-amber-100">
        <div className="min-w-0">
          <label className="text-[10px] font-bold text-amber-700 uppercase ml-1">{t('wizard.mapping.rules.format_source')} {isMissing(rule.params.from) && <span className="text-red-500">*</span>}</label>
          <select value={rule.params.from || ''} onChange={e => updateRuleParam(index, 'from', e.target.value)} className={`w-full truncate text-xs border rounded p-2 outline-none mt-1 bg-white ${isMissing(rule.params.from) ? 'border-red-300 focus:border-red-500' : 'focus:border-amber-500'}`}>
            <option value="">-- Seleccionar --</option>
            <option value="YYYY-MM-DD">ISO (2024-12-31)</option>
            <option value="DD/MM/YYYY">{t('wizard.mapping.options.visual_latam') || 'Visual LATAM (DD/MM/YYYY)'}</option>
            <option value="MM/DD/YYYY">{t('wizard.mapping.options.visual_us') || 'Visual US (MM/DD/YYYY)'}</option>
            <option value="UNIX_S">{t('wizard.mapping.options.unix_s') || 'Unix Epoch (Segs)'}</option>
            <option value="UNIX_MS">{t('wizard.mapping.options.unix_ms') || 'Unix Epoch (Milisegs)'}</option>
            <option value="EXCEL">{t('wizard.mapping.options.excel_serial') || 'Num Serial (Excel)'}</option>
            <option value="CUSTOM">{t('wizard.mapping.rules.custom')}</option>
          </select>
          {rule.params.from === 'CUSTOM' && <input type="text" placeholder="Ej: DD-MM-YY" value={rule.params.custom_from || ''} onChange={e => updateRuleParam(index, 'custom_from', e.target.value)} className="w-full text-xs border rounded p-2 outline-none mt-1 focus:border-amber-500" />}
        </div>
        <div className="min-w-0">
          <label className="text-[10px] font-bold text-amber-700 uppercase ml-1">{t('wizard.mapping.rules.format_target')} {isMissing(rule.params.to) && <span className="text-red-500">*</span>}</label>
          <select value={rule.params.to || ''} onChange={e => updateRuleParam(index, 'to', e.target.value)} className={`w-full truncate text-xs border rounded p-2 outline-none mt-1 bg-white ${isMissing(rule.params.to) ? 'border-red-300 focus:border-red-500' : 'focus:border-amber-500'}`}>
            <option value="">-- Seleccionar --</option>
            <option value="YYYY-MM-DD">{t('wizard.mapping.options.db_std') || 'Estándar DB (YYYY-MM-DD)'}</option>
            <option value="DD/MM/YYYY">{t('wizard.mapping.options.visual_latam') || 'Visual LATAM (DD/MM/YYYY)'}</option>
            <option value="UNIX_S">{t('wizard.mapping.options.unix_s') || 'Unix Epoch (Segs)'}</option>
            <option value="UNIX_MS">{t('wizard.mapping.options.unix_ms') || 'Unix Epoch (Milisegs)'}</option>
            <option value="CUSTOM">{t('wizard.mapping.rules.custom')}</option>
          </select>
          {rule.params.to === 'CUSTOM' && <input type="text" placeholder="Ej: YYYY/MM/DD" value={rule.params.custom_to || ''} onChange={e => updateRuleParam(index, 'custom_to', e.target.value)} className="w-full text-xs border rounded p-2 outline-none mt-1 focus:border-amber-500" />}
        </div>
      </div>
    );
    if (rule.id === 'ADVANCED_COND') return (
      <div className="grid grid-cols-1 gap-2 mt-1 bg-indigo-50/50 p-2 rounded-lg border border-indigo-100">
        <div className="min-w-0">
          <label className="text-[10px] font-bold text-indigo-700 uppercase ml-1">{t('wizard.mapping.rules.logic_op')} {isMissing(rule.params.operator) && <span className="text-red-500">*</span>}</label>
          <select value={rule.params.operator || ''} onChange={e => updateRuleParam(index, 'operator', e.target.value)} className={`w-full truncate text-xs border rounded p-2 outline-none mt-1 bg-white ${isMissing(rule.params.operator) ? 'border-red-300 focus:border-red-500' : 'focus:border-indigo-500'}`}>
            <option value="">-- Seleccionar --</option>
            <option value="IS_EMPTY">{t('wizard.mapping.options.is_empty') || 'Si está Vacío'}</option>
            <option value="NOT_EMPTY">{t('wizard.mapping.options.not_empty') || 'Si NO está Vacío'}</option>
            <option value="EQUALS">{t('wizard.mapping.options.equals') || 'Si es Igual a (=)'}</option>
            <option value="NOT_EQUALS">{t('wizard.mapping.options.not_equals') || 'Si es Diferente a (<>)'}</option>
            <option value="GREATER">{t('wizard.mapping.options.greater') || 'Si es Mayor que (>)'}</option>
            <option value="LESS">{t('wizard.mapping.options.less') || 'Si es Menor que (<)'}</option>
            <option value="CONTAINS">{t('wizard.mapping.options.contains') || 'Si Contiene'}</option>
            <option value="NOT_CONTAINS">{t('wizard.mapping.options.not_contains') || 'Si NO Contiene'}</option>
            <option value="STARTS_WITH">{t('wizard.mapping.options.starts_with') || 'Si Comienza con'}</option>
            <option value="ENDS_WITH">{t('wizard.mapping.options.ends_with') || 'Si Termina con'}</option>
          </select>
        </div>
        {!['IS_EMPTY', 'NOT_EMPTY'].includes(rule.params.operator) && (
          <div>
            <label className="text-[10px] font-bold text-indigo-700 uppercase ml-1">{t('wizard.mapping.rules.eval_val')} {isMissing(rule.params.condition_val) && <span className="text-red-500">*</span>}</label>
            <input type="text" placeholder="Ej: Casa, 100" value={rule.params.condition_val || ''} onChange={e => updateRuleParam(index, 'condition_val', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.condition_val) ? 'border-red-300 focus:border-red-500' : 'focus:border-indigo-500'}`} />
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 mt-1">
          <div className="min-w-0">
            <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1">{t('wizard.mapping.rules.then_assign')}: {isMissing(rule.params.result) && <span className="text-red-500">*</span>}</label>
            <input type="text" placeholder="Resultado..." value={rule.params.result || ''} onChange={e => updateRuleParam(index, 'result', e.target.value)} className={`w-full text-xs rounded p-2 outline-none mt-1 border ${isMissing(rule.params.result) ? 'border-red-300 focus:border-red-500' : 'border-emerald-300 focus:border-emerald-500'}`} />
          </div>

          <div className="min-w-0">
            <label className="text-[10px] font-bold text-slate-700 uppercase ml-1">{t('wizard.mapping.rules.else')}:</label>
            <div className="flex flex-col gap-1 mt-1">
              <select
                value={rule.params.else_action || 'KEEP'}
                onChange={e => {
                  const action = e.target.value;
                  updateRuleParam(index, 'else_action', action);
                  if (action === 'KEEP') updateRuleParam(index, 'else_result', null);
                  if (action === 'NULL') updateRuleParam(index, 'else_result', 'NULL');
                  if (action === 'EMPTY') updateRuleParam(index, 'else_result', '');
                  if (action === 'FIXED') updateRuleParam(index, 'else_result', '');
                }}
                className="w-full text-xs rounded p-2 outline-none border border-slate-300 focus:border-indigo-500 bg-white"
              >
                <option value="KEEP">{t('wizard.mapping.rules.keep')}</option>
                <option value="NULL">{t('wizard.mapping.rules.assign_null')}</option>
                <option value="EMPTY">{t('wizard.mapping.rules.leave_empty')}</option>
                <option value="FIXED">{t('wizard.mapping.rules.assign_fixed')}</option>
              </select>

              {rule.params.else_action === 'FIXED' && (
                <input
                  type="text"
                  placeholder={t('wizard.mapping.rules.write_val')}
                  value={rule.params.else_result || ''}
                  onChange={e => updateRuleParam(index, 'else_result', e.target.value)}
                  className="w-full text-xs rounded p-2 outline-none border border-slate-300 focus:border-indigo-500 animate-in fade-in slide-in-from-top-1"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
    if (rule.id === 'SET_FIXED') return (
      <div className="mt-1 bg-blue-50/50 p-2 rounded-lg border border-blue-100">
        <label className="text-[10px] font-bold text-blue-700 uppercase ml-1">{t('wizard.mapping.rules.fixed_val')} {isMissing(rule.params.value) && <span className="text-red-500">*</span>}</label>
        <input type="text" placeholder="Ej: Perú, 2024..." value={rule.params.value || ''} onChange={e => updateRuleParam(index, 'value', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.value) ? 'border-red-300 focus:border-red-500' : 'focus:border-blue-500'}`} />
      </div>
    );
    if (rule.id === 'REMOVE_ACCENTS') return (
      <div className="mt-1 bg-emerald-50 p-2 rounded-lg border border-emerald-100">
        <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1">{t('wizard.mapping.rules.preserve_n')}</label>
        <select value={rule.params.preserveÑ || 'true'} onChange={e => updateRuleParam(index, 'preserveÑ', e.target.value)} className="w-full text-xs border rounded p-2 outline-none mt-1 bg-white focus:border-emerald-500">
          <option value="true">{t('wizard.mapping.rules.yes_preserve_n')}</option>
          <option value="false">{t('wizard.mapping.rules.no_remove_all')}</option>
        </select>
      </div>
    );
    if (rule.id === 'PAD_LEFT' || rule.id === 'PAD_RIGHT') return (
      <div className="grid grid-cols-2 gap-2 mt-1 bg-emerald-50 p-2 rounded-lg border border-emerald-100 items-end">
        <div className="min-w-0 flex flex-col">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1 mb-1">{t('wizard.mapping.rules.pad_char')}</label>
          <input type="text" maxLength={1} placeholder="0" value={rule.params.padChar || ''} onChange={e => updateRuleParam(index, 'padChar', e.target.value)} className="w-full text-xs border rounded p-2 outline-none focus:border-emerald-500" />
        </div>
        <div className="min-w-0 flex flex-col">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1 mb-1">{t('wizard.mapping.rules.total_length')} {isMissing(rule.params.total_length) && <span className="text-red-500">*</span>}</label>
          <input type="text" placeholder="Ej: 6" value={rule.params.total_length || ''} onChange={e => handleStrictInt(index, 'total_length', e.target.value, 255)} className={`w-full text-xs border rounded p-2 outline-none ${isMissing(rule.params.total_length) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`} />
        </div>
      </div>
    );
    if (rule.id === 'FORMAT_CODE') return (
      <div className="grid grid-cols-3 gap-2 mt-1 bg-emerald-50 p-2 rounded-lg border border-emerald-100 items-end">
        <div className="min-w-0 flex flex-col">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1 mb-1">{t('wizard.mapping.rules.code_prefix')} {isMissing(rule.params.prefix) && <span className="text-red-500">*</span>}</label>
          <input type="text" placeholder="PRD" value={rule.params.prefix || ''} onChange={e => updateRuleParam(index, 'prefix', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none ${isMissing(rule.params.prefix) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`} />
        </div>
        <div className="min-w-0 flex flex-col">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1 mb-1">{t('wizard.mapping.rules.pad_char')}</label>
          <input type="text" maxLength={1} placeholder="0" value={rule.params.padChar || ''} onChange={e => updateRuleParam(index, 'padChar', e.target.value)} className="w-full text-xs border rounded p-2 outline-none focus:border-emerald-500" />
        </div>
        <div className="min-w-0 flex flex-col">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1 mb-1">{t('wizard.mapping.rules.total_length')} {isMissing(rule.params.totalLength) && <span className="text-red-500">*</span>}</label>
          <input type="text" placeholder="8" value={rule.params.totalLength || ''} onChange={e => handleStrictInt(index, 'totalLength', e.target.value, 255)} className={`w-full text-xs border rounded p-2 outline-none ${isMissing(rule.params.totalLength) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`} />
        </div>
      </div>
    );
    if (rule.id === 'STRIP_CODE') return (
      <div className="grid grid-cols-2 gap-2 mt-1 bg-emerald-50 p-2 rounded-lg border border-emerald-100 items-end">
        <div className="min-w-0 flex flex-col">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1 mb-1">{t('wizard.mapping.rules.code_prefix')} {isMissing(rule.params.prefix) && <span className="text-red-500">*</span>}</label>
          <input type="text" placeholder="PRD" value={rule.params.prefix || ''} onChange={e => updateRuleParam(index, 'prefix', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none ${isMissing(rule.params.prefix) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`} />
        </div>
        <div className="min-w-0 flex items-end pb-1">
          <label className="flex items-center gap-2 text-xs text-emerald-700 cursor-pointer">
            <input type="checkbox" checked={rule.params.stripLeadingZeros !== 'false'} onChange={e => updateRuleParam(index, 'stripLeadingZeros', e.target.checked ? 'true' : 'false')} className="rounded border-emerald-300" />
            {t('wizard.mapping.rules.strip_zeros')}
          </label>
        </div>
      </div>
    );
    if (rule.id === 'SPLIT_FIELD') return (
      <div className="grid grid-cols-2 gap-2 mt-1 bg-emerald-50 p-2 rounded-lg border border-emerald-100 items-end">
        <div className="min-w-0 flex flex-col">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1 mb-1">{t('wizard.mapping.rules.separator')} {isMissing(rule.params.separator) && <span className="text-red-500">*</span>}</label>
          <input type="text" placeholder="Ej: espacio, - , ;" value={rule.params.separator || ''} onChange={e => updateRuleParam(index, 'separator', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none ${isMissing(rule.params.separator) ? 'border-red-300 focus:border-red-500' : 'focus:border-emerald-500'}`} />
        </div>
        <div className="min-w-0 flex flex-col">
          <label className="text-[10px] font-bold text-emerald-700 uppercase ml-1 mb-1">{t('wizard.mapping.rules.part_index')}</label>
          <input type="text" placeholder="0" value={rule.params.index ?? ''} onChange={e => handleStrictInt(index, 'index', e.target.value, 99)} className="w-full text-xs border rounded p-2 outline-none focus:border-emerald-500" />
        </div>
      </div>
    );
    if (rule.id === 'SET_CURRENT_DATE') return (
      <div className="grid grid-cols-2 gap-2 mt-1 bg-amber-50/30 p-2 rounded-lg border border-amber-100 items-end">
        <div className="min-w-0 flex flex-col">
          <label className="text-[10px] font-bold text-amber-700 uppercase ml-1 mb-1">{t('wizard.mapping.rules.date_format')} {isMissing(rule.params.dateFormat) && <span className="text-red-500">*</span>}</label>
          <select value={rule.params.dateFormat || ''} onChange={e => updateRuleParam(index, 'dateFormat', e.target.value)} className={`w-full text-xs border rounded p-2 outline-none bg-white ${isMissing(rule.params.dateFormat) ? 'border-red-300 focus:border-red-500' : 'focus:border-amber-500'}`}>
            <option value="">-- {t('wizard.mapping.rules.select')} --</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD HH:mm:ss">YYYY-MM-DD HH:mm:ss</option>
            <option value="UNIX_S">{t('wizard.mapping.options.unix_s') || 'Unix Epoch (Segs)'}</option>
            <option value="UNIX_MS">{t('wizard.mapping.options.unix_ms') || 'Unix Epoch (Milisegs)'}</option>
          </select>
        </div>
        <div className="min-w-0 flex flex-col">
          <label className="text-[10px] font-bold text-amber-700 uppercase ml-1 mb-1">{t('wizard.mapping.rules.offset_days')}</label>
          <input type="number" placeholder="0" value={rule.params.offsetDays ?? ''} onChange={e => updateRuleParam(index, 'offsetDays', e.target.value)} className="w-full text-xs border rounded p-2 outline-none focus:border-amber-500" />
        </div>
      </div>
    );
    if (rule.id === 'TRANSLATE_VALUE') return (
      <div className="mt-1 bg-indigo-50/50 p-3 rounded-lg border border-indigo-100 flex flex-col gap-3">
        <div>
          <label className="text-[10px] font-bold text-indigo-700 uppercase ml-1">{t('wizard.mapping.rules.equivalences')} {(!rule.params.mappings || (typeof rule.params.mappings === 'string' && rule.params.mappings.trim() === '') || (typeof rule.params.mappings === 'object' && Object.keys(rule.params.mappings).length === 0)) && <span className="text-red-500">*</span>}</label>
          {/* <textarea placeholder={t('wizard.mapping.rules.translate_placeholder')} rows={4} value={typeof rule.params.mappings === 'object' ? Object.entries(rule.params.mappings).map(([k, v]) => `${k}=${v}`).join('\n') : (rule.params.mappings || '')} onChange={e => { */}
          <textarea placeholder={t('wizard.mapping.rules.translate_placeholder')} rows={4} value={typeof rule.params.mappings === 'object' ? Object.entries(rule.params.mappings).map(([k, v]) => `${k}=${v}`).join('\n') : (rule.params.mappings || '')} onChange={e => {
            updateRuleParam(index, 'mappings', e.target.value);
          }} onBlur={e => {
            const raw = e.target.value;
            const obj = {};
            raw.split('\n').forEach(line => {
              const eqIdx = line.indexOf('=');
              if (eqIdx > 0) obj[line.substring(0, eqIdx).trim()] = line.substring(eqIdx + 1).trim();
            });
            if (Object.keys(obj).length > 0) updateRuleParam(index, 'mappings', obj);
          }} className={`w-full text-xs border rounded p-2 outline-none mt-1 font-mono ${(!rule.params.mappings || (typeof rule.params.mappings === 'string' && rule.params.mappings.trim() === '') || (typeof rule.params.mappings === 'object' && Object.keys(rule.params.mappings).length === 0)) ? 'border-red-300 focus:border-red-500' : 'focus:border-indigo-500'}`} />
          <p className="text-[10px] text-indigo-500 mt-1 ml-1">{t('wizard.mapping.rules.equivalences_hint')}</p>
        </div>
        <div>
          <label className="text-[10px] font-bold text-indigo-700 uppercase ml-1">{t('wizard.mapping.rules.default_when_no_match')}</label>
          <input type="text" placeholder={t('wizard.mapping.rules.optional')} value={rule.params.default || ''} onChange={e => updateRuleParam(index, 'default', e.target.value)} className="w-full text-xs border rounded p-2 outline-none mt-1 focus:border-indigo-500" />
        </div>
      </div>
    );

    if (rule.id === 'LOOKUP') {
      const selectedTable = rule.params.lookupTable || '';
      const cols = lookupColumnsCache[selectedTable] || [];
      const hasTable = !!selectedTable;
      const hasCols = cols.length > 0;
      const filterActive = !!rule.params.filterField;

      return (
        <div className="mt-1 bg-cyan-50/50 p-3 rounded-lg border border-cyan-200 flex flex-col gap-3">
          {/* ── Tabla de búsqueda ── */}
          <div>
            <label className="text-[10px] font-bold text-cyan-700 uppercase ml-1">
              {t('wizard.mapping.rules.lookup_table')} {isMissing(rule.params.lookupTable) && <span className="text-red-500">*</span>}
            </label>
            <select
              value={selectedTable}
              onChange={e => handleLookupTableChange(index, e.target.value)}
              className={`w-full text-xs border rounded p-2 outline-none mt-1 bg-white ${isMissing(rule.params.lookupTable) ? 'border-red-300 focus:border-red-500' : 'focus:border-cyan-500'}`}
            >
              <option value="">-- {t('wizard.mapping.rules.select')} --</option>
              {(targetAvailableTables || []).map(tbl => (
                <option key={tbl} value={tbl}>{tbl}</option>
              ))}
            </select>
          </div>

          {/* ── Campo de búsqueda + Campo a retornar ── */}
          {hasTable && (
            <div className="grid grid-cols-2 gap-2 items-end">
              <div className="min-w-0 flex flex-col">
                <label className="text-[10px] font-bold text-cyan-700 uppercase ml-1 mb-auto">
                  {t('wizard.mapping.rules.lookup_field')} {isMissing(rule.params.lookupField) && <span className="text-red-500">*</span>}
                </label>
                {loadingLookupCols ? (
                  <div className="text-xs text-cyan-500 mt-1 p-2">{t('common.loading')}...</div>
                ) : hasCols ? (
                  <select
                    value={rule.params.lookupField || ''}
                    onChange={e => updateRuleParam(index, 'lookupField', e.target.value)}
                    className={`w-full text-xs border rounded p-2 outline-none mt-1 bg-white ${isMissing(rule.params.lookupField) ? 'border-red-300 focus:border-red-500' : 'focus:border-cyan-500'}`}
                  >
                    <option value="">-- {t('wizard.mapping.rules.select')} --</option>
                    {cols.map(col => (
                      <option key={col.name || col} value={col.name || col}>{col.name || col}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder={t('wizard.mapping.rules.lookup_field_hint')}
                    value={rule.params.lookupField || ''}
                    onChange={e => updateRuleParam(index, 'lookupField', e.target.value)}
                    className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.lookupField) ? 'border-red-300 focus:border-red-500' : 'focus:border-cyan-500'}`}
                  />
                )}
              </div>

              <div className="min-w-0 flex flex-col">
                <label className="text-[10px] font-bold text-cyan-700 uppercase ml-1 mb-auto">
                  {t('wizard.mapping.rules.return_field')} {isMissing(rule.params.returnField) && <span className="text-red-500">*</span>}
                </label>
                {loadingLookupCols ? (
                  <div className="text-xs text-cyan-500 mt-1 p-2">{t('common.loading')}...</div>
                ) : hasCols ? (
                  <select
                    value={rule.params.returnField || ''}
                    onChange={e => updateRuleParam(index, 'returnField', e.target.value)}
                    className={`w-full text-xs border rounded p-2 outline-none mt-1 bg-white ${isMissing(rule.params.returnField) ? 'border-red-300 focus:border-red-500' : 'focus:border-cyan-500'}`}
                  >
                    <option value="">-- {t('wizard.mapping.rules.select')} --</option>
                    {cols.map(col => (
                      <option key={col.name || col} value={col.name || col}>{col.name || col}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder={t('wizard.mapping.rules.return_field_hint')}
                    value={rule.params.returnField || ''}
                    onChange={e => updateRuleParam(index, 'returnField', e.target.value)}
                    className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.returnField) ? 'border-red-300 focus:border-red-500' : 'focus:border-cyan-500'}`}
                  />
                )}
              </div>
            </div>
          )}

          {/* ── Filtro contextual (opcional) ── */}
          {hasTable && (
            <div className="border-t border-cyan-200 pt-2">
              <label className="text-[10px] font-bold text-cyan-700 uppercase ml-1">
                {t('wizard.mapping.rules.lookup_filter_field') || 'Filtrar resultados por (opcional)'}
              </label>
              {hasCols ? (
                <select
                  value={rule.params.filterField || ''}
                  onChange={e => {
                    updateRuleParam(index, 'filterField', e.target.value);
                    if (!e.target.value) {
                      updateRuleParam(index, 'filterSource', '');
                      updateRuleParam(index, 'filterValue', '');
                    } else if (!rule.params.filterSource) {
                      updateRuleParam(index, 'filterSource', 'fixed');
                    }
                  }}
                  className="w-full text-xs border rounded p-2 outline-none mt-1 bg-white focus:border-cyan-500"
                >
                  <option value="">{t('wizard.mapping.rules.lookup_no_filter') || '— Sin filtro —'}</option>
                  {cols.map(col => (
                    <option key={col.name || col} value={col.name || col}>{col.name || col}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder={t('wizard.mapping.rules.lookup_filter_field_hint') || 'Nombre del campo filtro'}
                  value={rule.params.filterField || ''}
                  onChange={e => {
                    updateRuleParam(index, 'filterField', e.target.value);
                    if (!e.target.value) {
                      updateRuleParam(index, 'filterSource', '');
                      updateRuleParam(index, 'filterValue', '');
                    } else if (!rule.params.filterSource) {
                      updateRuleParam(index, 'filterSource', 'fixed');
                    }
                  }}
                  className="w-full text-xs border rounded p-2 outline-none mt-1 focus:border-cyan-500"
                />
              )}

              {filterActive && (
                <div className="grid grid-cols-2 gap-2 mt-2 items-end">
                  <div className="min-w-0 flex flex-col">
                    <label className="text-[10px] font-bold text-cyan-700 uppercase ml-1 mb-1">
                      {t('wizard.mapping.rules.lookup_filter_source') || 'Comparar con'}
                    </label>
                    <select
                      value={rule.params.filterSource || 'fixed'}
                      onChange={e => {
                        updateRuleParam(index, 'filterSource', e.target.value);
                        updateRuleParam(index, 'filterValue', '');
                      }}
                      className="w-full text-xs border rounded p-2 outline-none bg-white focus:border-cyan-500"
                    >
                      <option value="fixed">{t('wizard.mapping.rules.lookup_filter_fixed') || 'Valor fijo'}</option>
                      <option value="column">{t('wizard.mapping.rules.lookup_filter_column') || 'Columna del origen'}</option>
                    </select>
                  </div>

                  <div className="min-w-0 flex flex-col">
                    <label className="text-[10px] font-bold text-cyan-700 uppercase ml-1 mb-1">
                      {t('wizard.mapping.rules.lookup_filter_value') || 'Valor'} {isMissing(rule.params.filterValue) && <span className="text-red-500">*</span>}
                    </label>
                    {rule.params.filterSource === 'column' ? (
                      <select
                        value={rule.params.filterValue || ''}
                        onChange={e => updateRuleParam(index, 'filterValue', e.target.value)}
                        className={`w-full text-xs border rounded p-2 outline-none bg-white ${isMissing(rule.params.filterValue) ? 'border-red-300 focus:border-red-500' : 'focus:border-cyan-500'}`}
                      >
                        <option value="">-- {t('wizard.mapping.rules.select')} --</option>
                        {(sourceColumns || []).map(col => (
                          <option key={col.name} value={col.name}>{col.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder={t('wizard.mapping.rules.lookup_filter_value_hint') || 'Ej: 5'}
                        value={rule.params.filterValue || ''}
                        onChange={e => updateRuleParam(index, 'filterValue', e.target.value)}
                        className={`w-full text-xs border rounded p-2 outline-none ${isMissing(rule.params.filterValue) ? 'border-red-300 focus:border-red-500' : 'focus:border-cyan-500'}`}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Qué hacer si no encuentra ── */}
          {hasTable && (
            <div>
              <label className="text-[10px] font-bold text-cyan-700 uppercase ml-1">
                {t('wizard.mapping.rules.on_not_found')}
              </label>
              <select
                value={rule.params.onNotFound || 'null'}
                onChange={e => {
                  updateRuleParam(index, 'onNotFound', e.target.value);
                  if (e.target.value !== 'default') updateRuleParam(index, 'defaultValue', '');
                }}
                className="w-full text-xs border rounded p-2 outline-none mt-1 bg-white focus:border-cyan-500"
              >
                <option value="null">{t('wizard.mapping.rules.lookup_null')}</option>
                <option value="default">{t('wizard.mapping.rules.lookup_default') || 'Usar valor por defecto'}</option>
                <option value="error">{t('wizard.mapping.rules.lookup_error')}</option>
                <option value="skip">{t('wizard.mapping.rules.lookup_skip')}</option>
              </select>

              {rule.params.onNotFound === 'default' && (
                <input
                  type="text"
                  placeholder={t('wizard.mapping.rules.lookup_default_hint') || 'Ej: 1'}
                  value={rule.params.defaultValue || ''}
                  onChange={e => updateRuleParam(index, 'defaultValue', e.target.value)}
                  className={`w-full text-xs border rounded p-2 outline-none mt-1 ${isMissing(rule.params.defaultValue) ? 'border-red-300 focus:border-red-500' : 'focus:border-cyan-500'}`}
                />
              )}

              <p className="text-[10px] text-cyan-500 mt-1 ml-1">
                {rule.params.onNotFound === 'skip'
                  ? t('wizard.mapping.rules.lookup_skip_hint')
                  : rule.params.onNotFound === 'error'
                    ? t('wizard.mapping.rules.lookup_error_hint')
                    : rule.params.onNotFound === 'default'
                      ? (t('wizard.mapping.rules.lookup_default_desc') || 'Si no encuentra equivalencia, asigna el valor por defecto configurado.')
                      : t('wizard.mapping.rules.lookup_null_hint')
                }
              </p>
            </div>
          )}
        </div>
      );
    }

    if (rule.id === 'JSON_EXTRACT') {
      return (
        <div className="mt-1 bg-violet-50/50 p-3 rounded-lg border border-violet-200 flex flex-col gap-2">
          <div>
            <label className="text-[10px] font-bold text-violet-700 uppercase ml-1">
              {t('wizard.mapping.rules.json_extract_path')} {isMissing(rule.params.path) && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              placeholder="address.city, metadata.campaign..."
              value={rule.params.path || ''}
              onChange={e => updateRuleParam(index, 'path', e.target.value)}
              className={`w-full text-xs border rounded p-2 outline-none mt-1 font-mono ${isMissing(rule.params.path) ? 'border-red-300 focus:border-red-500' : 'focus:border-violet-500'}`}
            />
            <p className="text-[10px] text-violet-500 mt-1 ml-1">{t('wizard.mapping.rules.json_extract_hint')}</p>
          </div>
        </div>
      );
    }

    return null;
  };

  const activeHasFixed = localMap[activeTransform]?.pipeline?.some(r => r.id === 'SET_FIXED');

  const renderCatalogGroup = (id, title, icon, colorClass, borderClass, bgHover) => (
    <div className={`border ${borderClass} rounded-xl overflow-hidden`}>
      <button onClick={() => toggleCategory(id)} className={`w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition`}>
        <span className={`text-xs font-bold ${colorClass} uppercase flex items-center`}>{icon} {title}</span>
        {openCategories[id] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {openCategories[id] && (
        <div className="p-2 grid grid-cols-1 gap-1.5 bg-white">
          {ruleCatalog[id].map(rule => (
            <button key={rule.id} disabled={activeHasFixed} onClick={() => requestAddRule(rule)} className={`text-left px-3 py-2 text-xs font-medium bg-white border border-slate-200 rounded-lg transition-all flex justify-between items-center group ${activeHasFixed ? 'opacity-40 cursor-not-allowed' : `hover:${borderClass} hover:${colorClass} hover:${bgHover}`}`}>
              {/* 🟢 MAGIA: Traducimos por ID dinámicamente, si falla, usa el texto por defecto */}
              {t(`wizard.mapping.rule_names.${rule.id}`) || rule.label} <Plus className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[60] p-2 md:p-4 animate-in fade-in">

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden animate-in zoom-in-95 flex flex-col h-[90vh] md:h-[80vh]">

        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-white shrink-0 shadow-sm z-10">
          <h3 className="font-bold text-slate-800 flex items-center text-sm md:text-base">
            <Settings2 className="w-5 h-5 mr-2 text-indigo-600 hidden md:block" />
            {t('wizard.mapping.rules_for')} <span className="font-mono ml-2 text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded break-all">{activeTransform}</span>
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"><X className="w-6 h-6" /></button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden bg-slate-50/50">

          <div className="flex-1 overflow-y-auto border-b md:border-b-0 md:border-r border-slate-200 p-4 custom-scrollbar">
            <h4 className="text-xs font-bold text-slate-500 uppercase mb-4 flex items-center"><Layers className="w-4 h-4 mr-2" /> {t('wizard.mapping.seq_title')}</h4>

            {(!localMap[activeTransform]?.pipeline || localMap[activeTransform].pipeline.length === 0) ? (
              <div className="text-center py-12 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-lg bg-white">
                {t('wizard.mapping.no_rules_part1')}<br />{t('wizard.mapping.no_rules_part2')}
              </div>
            ) : (
              <div className="space-y-3 pb-8">
                {localMap[activeTransform].pipeline.map((rule, index) => (
                  <div key={index} className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm flex flex-col relative group">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[11px] md:text-xs font-bold text-slate-700 flex items-center">
                        <span className="bg-slate-800 text-white w-5 h-5 rounded-full flex items-center justify-center text-[10px] mr-2 shrink-0">{index + 1}</span>
                        {/* 🟢 MAGIA: Traducimos en la secuencia visual usando el ID guardado */}
                        {t(`wizard.mapping.rule_names.${rule.id}`) || rule.label}
                      </span>
                      <div className="flex items-center gap-1">
                        {RULE_EXAMPLES[rule.id] && (
                          <button
                            onClick={() => setOpenExampleIdx(openExampleIdx === index ? null : index)}
                            className={`p-1 rounded transition ${openExampleIdx === index ? 'text-amber-600 bg-amber-50' : 'text-amber-400 hover:bg-amber-50 hover:text-amber-600'}`}
                            title={t('wizard.mapping.rules.show_example') || 'Ver ejemplo'}
                          >
                            <Lightbulb className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => moveRule(index, -1)} disabled={index === 0} className="p-1 text-slate-400 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                        <button onClick={() => moveRule(index, 1)} disabled={index === localMap[activeTransform].pipeline.length - 1} className="p-1 text-slate-400 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                        <div className="w-px h-4 bg-slate-200 mx-1"></div>
                        <button onClick={() => removeRuleFromPipeline(index)} className="p-1 text-red-400 hover:bg-red-50 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>

                    {openExampleIdx === index && RULE_EXAMPLES[rule.id] && (() => {
                      const ex = RULE_EXAMPLES[rule.id];
                      const k = `wizard.mapping.rule_examples.${rule.id}`;
                      const tx = (path, fallback) => { const v = t(path); return v && v !== path ? v : fallback; };
                      return (
                        <div className="mb-2 bg-amber-50/70 border border-amber-200 rounded-lg px-3 py-2 text-[11px] space-y-1">
                          <div className="flex items-start gap-2">
                            <span className="text-amber-500 font-bold shrink-0">📥</span>
                            <span className="text-slate-700 break-all">
                              <span className="font-mono">{tx(`${k}.input`) || ex.input}</span>
                              {ex.inputLabel && <span className="text-slate-400 italic ml-1.5">({tx(`${k}.inputLabel`) || ex.inputLabel})</span>}
                            </span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-emerald-500 font-bold shrink-0">📤</span>
                            <span className="text-emerald-700 break-all">
                              <span className="font-mono font-bold">{tx(`${k}.output`) || ex.output}</span>
                              {ex.outputLabel && <span className="text-emerald-500 italic font-normal ml-1.5">({tx(`${k}.outputLabel`) || ex.outputLabel})</span>}
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-500 leading-relaxed border-t border-amber-100 pt-1">
                            {tx(`${k}.note`) || ex.note}
                          </p>
                        </div>
                      );
                    })()}

                    {renderRuleInputs(rule, index)}

                    {index < localMap[activeTransform].pipeline.length - 1 && (
                      <div className="absolute -bottom-4 left-5 text-slate-300 z-10 bg-slate-50"><ArrowDown className="w-5 h-5" /></div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white pb-8 custom-scrollbar">
            {renderCatalogGroup('cond', t('wizard.mapping.cat_cond'), <Brackets className="w-4 h-4 mr-2" />, 'text-indigo-700', 'border-indigo-200', 'bg-indigo-50')}
            {renderCatalogGroup('text', t('wizard.mapping.cat_text'), <Scissors className="w-4 h-4 mr-2" />, 'text-emerald-700', 'border-emerald-200', 'bg-emerald-50')}
            {renderCatalogGroup('number', t('wizard.mapping.cat_num'), <Hash className="w-4 h-4 mr-2" />, 'text-blue-700', 'border-blue-200', 'bg-blue-50')}
            {renderCatalogGroup('date', t('wizard.mapping.cat_date'), <Calendar className="w-4 h-4 mr-2" />, 'text-amber-700', 'border-amber-200', 'bg-amber-50')}
            {renderCatalogGroup('lookup', t('wizard.mapping.cat_lookup'), <Database className="w-4 h-4 mr-2" />, 'text-cyan-700', 'border-cyan-200', 'bg-cyan-50')}
            {renderCatalogGroup('security', t('wizard.mapping.cat_sec'), <Shield className="w-4 h-4 mr-2" />, 'text-slate-700', 'border-slate-300', 'bg-slate-100')}
            {renderCatalogGroup('fixed', t('wizard.mapping.cat_fixed'), <Pin className="w-4 h-4 mr-2" />, 'text-slate-700', 'border-slate-300', 'bg-slate-100')}
            {ruleCatalog['extract'] && renderCatalogGroup('extract', t('wizard.mapping.cat_extract'), <Layers className="w-4 h-4 mr-2" />, 'text-violet-700', 'border-violet-200', 'bg-violet-50')}
          </div>
        </div>

        {/* ── Vista previa del pipeline ── */}
        {(localMap[activeTransform]?.pipeline || []).length > 0 && localMap[activeTransform]?.source && localMap[activeTransform].source !== 'IGNORE' && (
          <div className="px-3 md:px-4 pb-2">
            <PipelinePreview
              pipeline={localMap[activeTransform]?.pipeline || []}
              sourceColumn={localMap[activeTransform]?.source}
              // sourceConfig={{ type: source?.type, config: source?.config }}
              // targetConfig={{ type: target?.type, config: target?.config }}
              sourceConfig={{ type: source?.type, config: { ...source?.config, _idConfigHead: useWizardStore.getState().editId } }}
              targetConfig={{ type: target?.type, config: { ...target?.config, _idConfigHead: useWizardStore.getState().editId } }}
              onAddSuggestedRule={(ruleId) => {
                setLocalMap(prev => {
                  const colData = prev[activeTransform];
                  const pipeline = [...(colData.pipeline || [])];
                  const lookupIdx = pipeline.findIndex(r => r.id === 'LOOKUP');
                  const newRule = { id: ruleId, label: ruleId, params: {} };
                  if (lookupIdx >= 0) {
                    pipeline.splice(lookupIdx, 0, newRule);
                  } else {
                    pipeline.push(newRule);
                  }
                  return { ...prev, [activeTransform]: { ...colData, type: 'transform', pipeline } };
                });
              }}
            />
          </div>
        )}

        <div className="p-3 md:p-4 border-t border-slate-200 bg-slate-50 flex justify-end shrink-0 gap-2 shadow-[0_-4px_6px_-1px_rgb(0,0,0,0.05)] z-10">
          <button onClick={onClearPipeline} className="px-4 py-2 text-slate-600 hover:bg-slate-200 font-bold rounded-lg transition-all text-xs md:text-sm border border-slate-300 bg-white shadow-sm">{t('wizard.mapping.btn_clear')}</button>
          <button onClick={onClose} className="px-6 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 shadow-sm transition-all text-xs md:text-sm">{t('wizard.mapping.btn_save')}</button>
        </div>
      </div>
    </div>
  );
}